/* eslint-disable react-hooks/exhaustive-deps */
import {
  IonContent,
  IonHeader,
  IonPage,
  IonPopover,
  IonToolbar,
  useIonRouter,
} from "@ionic/react";
import { Center, HStack, VStack } from "../components/utils";
import { useEffect, useState } from "react";
import { useDashboard } from "../queries/dashboard";
import { ChevronLeft, MoreVerticalFill } from "akar-icons";
import { Icon } from "@iconify/react";
import { format } from "date-fns";
import _ from "lodash";
import { EmployerState, Grant, GrantEventType } from "../types/onBoarding";
import { useCompany } from "../store/useCompany";
import { CashOutDetails } from "../store/useEvaluateCashOutForm";
import { getTotalCash, getTaxPercentageNumber, getTaxPercentage } from "../utils";

const SeeCashOutDetails: React.FC = () => {
  const router = useIonRouter();
  const [isPopOverOpen, setIsPopOverOpen] = useState(false);
  const { data } = useDashboard();
  const name = router.routeInfo.pathname.split("/")[2];
  const { state: company } = useCompany();
  let employers = data?.data.employers.filter(employer => employer.state !== EmployerState.Offered) || [];
  if (company && company !== "All") {
    employers = employers.filter(employer => employer.name === company);
  }
  const grants = employers.map((emp) => emp.grants || []).flat() || [];
  const grantIdMap = new Map<number, Grant>(grants.map(g => ([g.id || -1, g])));
  const events = grants.map(g => g.events).flat();
  const filteredEvents = events.filter(e => e.name === name && e.type === GrantEventType.Cashout);
  const employer = employers.find(employer => employer.id === grants[0].employerId);
  const [exerciseDetails, setExerciseDetails] = useState<CashOutDetails>();

  useEffect(() => {
    const exerciseDetails: CashOutDetails = {
      dateOfExercise: filteredEvents[0]?.date,
      employerId: employer?.id || 0,
      totalCash: getTotalCash(filteredEvents, grantIdMap),
      exercisedOptions: _.sumBy(filteredEvents, (event => event.noOfOptions)),
      exerciseEvents: filteredEvents,
      fairMarketValue: filteredEvents[0]?.fairMarketValue || 0,
      taxSlab: filteredEvents[0]?.taxSlab || "",
      taxToBePaid: getTotalCash(filteredEvents, grantIdMap) * (getTaxPercentageNumber(filteredEvents[0]?.taxSlab || "") || 0) / 100,
      name: filteredEvents[0]?.name || "",
    }
    setExerciseDetails(exerciseDetails);
  }, [])
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <HStack className="items-center justify-between h-16 max-w-md mx-auto">
            {router.canGoBack() && (
              <ChevronLeft className="w-6" size={20} onClick={() => {
                router.push("/tabs/grants", "back");
              }} />
            )}
            <p className="text-primary text-lg font-semibold">{exerciseDetails?.name}</p>
            <button className="w-6" id="click-trigger" onClick={() => setIsPopOverOpen(true)}>
              <MoreVerticalFill strokeWidth={2} size={24} />
            </button>
            <IonPopover isOpen={isPopOverOpen} mode="md"
              onDidDismiss={e => setIsPopOverOpen(false)}
              trigger="click-trigger" triggerAction="click"
            >
              <IonContent class="ion-padding">
                <VStack className="items-center justify-center w-full gap-6 py-3 divide-y-0">
                  <HStack className="items-center justify-center w-full gap-4">
                    <Icon
                      icon="mdi:delete-outline"
                      className="focus:text-primary"
                      width="12"
                      height="14" />
                    <button
                      className="focus:text-primary font-medium text-black"
                    >
                      Delete
                    </button>
                  </HStack>
                </VStack>
              </IonContent>
            </IonPopover>
          </HStack>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div className="max-w-md mx-auto mb-5">
          <HStack className=" items-center justify-center gap-1 px-2 py-4">
            <Center className="w-[40px] h-[40px] bg-gray-100 rounded-full">
              {employer?.name.charAt(0)}
            </Center>
            <VStack>
              <h6 className=" text-dark px-2 text-base font-normal">
                {employer?.name}
              </h6>
              <h6 className=" text-grey px-2 text-xs font-normal">
                Dated: {format(new Date(exerciseDetails?.dateOfExercise || new Date()), "dd MMMM yyyy")}
              </h6>
            </VStack>
          </HStack>
          <VStack className=" items-center px-4 mt-2">
            <HStack className=" rounded-xl w-full py-3 text-center border divide-x-2">
              <VStack className="w-1/2 gap-1">
                <h6 className="text-dark text-3xl font-medium">
                  {exerciseDetails?.exercisedOptions}
                </h6>
                <h6 className="text-grey text-xs font-normal">Total no. of options</h6>
              </VStack>
              <VStack className="w-1/2 gap-1">
                <h6 className="text-dark text-3xl font-medium">
                  <span className="font-sans text-xl">{employer?.currency}</span>
                  {exerciseDetails?.fairMarketValue}
                </h6>
                <h6 className="text-grey text-xs font-normal">Price Per Option </h6>
              </VStack>
            </HStack>
          </VStack>
          <VStack className="px-4">
            <HStack className="justify-between mt-8">
              <span className="text-dark font-medium">Total Cash:</span>
              <span className="text-dark">{employer?.currency} {(exerciseDetails?.totalCash || 0).toFixed(2)}</span>
            </HStack>
            <span className="text-grey text-xs">No. of Options * (Price Per Options - Strike Price)</span>
            <div className=" bg-grey w-64 h-[1px] rounded-full mt-4" />
            <HStack className="mt-7 justify-between">
              <span className="text-dark font-medium">Tax Slab: </span>
              <span className="text-dark">{getTaxPercentage(exerciseDetails?.taxSlab || "")}</span>
            </HStack>
            <span className="text-grey text-xs">Inclusive of salary bracket + the amount cashing out </span>
            <div className=" bg-grey w-64 h-[1px] rounded-full mt-4" />
            <HStack className="justify-between mt-8">
              <span className="text-dark font-medium">Tax to be Paid: </span>
              <span className="text-dark">{employer?.currency} {(exerciseDetails?.taxToBePaid || 0).toFixed(2)}</span>
            </HStack>
            <span className="text-grey text-xs">This is only an estimate of tax based on the salary slab. Final tax may be different and will be intimated by your company.</span>
            <div className=" bg-grey w-64 h-[1px] rounded-full mt-4" />
            <HStack className="justify-between mt-8">
              <span className="text-dark font-medium">Cash to be Received:  </span>
              <span className="text-dark">{employer?.currency} {((exerciseDetails?.totalCash || 0) - (exerciseDetails?.taxToBePaid || 0)).toFixed(2)}</span>
            </HStack>
            <span className="text-grey text-xs">Total Cash - Tax</span>
          </VStack>
        </div>
      </IonContent>
    </IonPage >
  );
};

export default SeeCashOutDetails;
